import type {
  WidgetEntitiesResponse,
  WidgetEntity,
} from '@highimpact-frontend/api-types';
import {
  Autocomplete,
  Box,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  controlPanelSelectorBoxSx,
  FilterDefinition,
  FilterOption,
  getFilterOptionLabel,
  getFilterOptionsAt,
  matchesSelectedFilters,
  mergeOptionsByKey,
  renderEllipsizedOption,
  renderFilterTags,
  sameFilterOption,
  sanitizeFiltersByHierarchy,
  toFilterRecord,
  toFrenchGroupLabel,
  uniqueValues,
} from './selectorUi';
import { getWidgetEntityKey } from './sourceMappers';

// -- type definitions --

type EntityRecord = {
  entity: WidgetEntity;
  dimensions: Record<string, string>;
};

export interface V2EntitiesSelectorProps {
  data: WidgetEntitiesResponse;
  onEntitySelect?: (selected: WidgetEntity[]) => void;
  initialSelection?: WidgetEntity[];
  matchControlPanelStyle?: boolean;
  /** When false, only one entity can be selected. Defaults to true. */
  multiple?: boolean;
}

// -- helper functions --

const ENTITY_FILTER_FIELDS: ReadonlyArray<{
  key: string;
  label: string;
  readValue: (entity: WidgetEntity) => string | null | undefined;
}> = [
  {
    key: 'project',
    label: 'project',
    readValue: (entity) => entity.project_name,
  },
  { key: 'site', label: 'site', readValue: (entity) => entity.site_name },
  {
    key: 'timezone',
    label: 'timezone',
    readValue: (entity) => entity.timezone,
  },
];

function buildEntityRecords(entities: readonly WidgetEntity[]): EntityRecord[] {
  return entities.map((entity) => ({
    entity,
    dimensions: Object.fromEntries(
      ENTITY_FILTER_FIELDS.flatMap((field) => {
        const value = field.readValue(entity);
        return value ? [[field.key, value]] : [];
      })
    ),
  }));
}

function buildFilterDefinitions(
  records: readonly EntityRecord[]
): FilterDefinition[] {
  return ENTITY_FILTER_FIELDS.flatMap((field) => {
    const distinctValues = uniqueValues(
      records
        .map((record) => record.dimensions[field.key])
        .filter((value): value is string => Boolean(value))
    );

    if (distinctValues.length <= 1) {
      return [];
    }

    return [{ key: field.key, label: field.label }];
  });
}

function sameWidgetEntity(left: WidgetEntity, right: WidgetEntity): boolean {
  return getWidgetEntityKey(left) === getWidgetEntityKey(right);
}

function getEntityLabel(entity: WidgetEntity): string {
  return `${entity.project_name} - ${entity.site_name}`;
}

function filterEntitiesByActiveFilters(
  records: readonly EntityRecord[],
  filters: Record<string, string[]>,
  definitions: readonly FilterDefinition[]
): WidgetEntity[] {
  return records
    .filter((record) =>
      matchesSelectedFilters(record.dimensions, filters, definitions)
    )
    .map((record) => record.entity);
}

// -- main component --

/**
 * Selector for v2 project-site entity responses.
 */
export const V2EntitiesSelector: React.FC<V2EntitiesSelectorProps> = ({
  data,
  onEntitySelect,
  initialSelection,
  matchControlPanelStyle = false,
  multiple = true,
}) => {
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [selection, setSelection] = useState<WidgetEntity[]>([]);

  const records = useMemo(
    () => buildEntityRecords(data.entities),
    [data.entities]
  );
  const filterDefinitions = useMemo(
    () => buildFilterDefinitions(records),
    [records]
  );

  const filteredEntities = useMemo(
    () => filterEntitiesByActiveFilters(records, filters, filterDefinitions),
    [records, filters, filterDefinitions]
  );

  const allFilterOptions = useMemo(
    () =>
      filterDefinitions.flatMap((definition, index) =>
        getFilterOptionsAt(records, filterDefinitions, filters, index).map(
          (value) => ({
            key: definition.key,
            label: definition.label,
            value,
          })
        )
      ),
    [filterDefinitions, filters, records]
  );

  const selectedFilterOptions = useMemo(
    () =>
      filterDefinitions.flatMap((definition) =>
        (filters[definition.key] ?? []).map((value) => ({
          key: definition.key,
          label: definition.label,
          value,
        }))
      ),
    [filterDefinitions, filters]
  );

  useEffect(() => {
    if (initialSelection) {
      setSelection(initialSelection);
    }
  }, [initialSelection]);

  const handleFiltersChange = (selectedOptions: FilterOption[]) => {
    const candidateFilters = toFilterRecord(selectedOptions);
    const nextFilters = sanitizeFiltersByHierarchy(
      (index, activeFilters) =>
        getFilterOptionsAt(records, filterDefinitions, activeFilters, index),
      filterDefinitions,
      candidateFilters
    );
    setFilters(nextFilters);
  };

  const handleSelectionChange = (nextSelection: WidgetEntity[]) => {
    setSelection(nextSelection);
    onEntitySelect?.(nextSelection);
  };

  const entitySelector = (
    <Autocomplete
      multiple={multiple}
      options={mergeOptionsByKey(filteredEntities, selection, sameWidgetEntity)}
      value={multiple ? selection : selection[0] ?? null}
      getOptionLabel={getEntityLabel}
      isOptionEqualToValue={sameWidgetEntity}
      onChange={(_, value) => {
        if (multiple) {
          handleSelectionChange(value as WidgetEntity[]);
          return;
        }

        handleSelectionChange(value ? [value as WidgetEntity] : []);
      }}
      renderOption={(props, entity) =>
        renderEllipsizedOption(
          props,
          `${getEntityLabel(entity)}${
            entity.timezone ? ` (${entity.timezone})` : ''
          }`
        )
      }
      renderTags={
        multiple
          ? (values, getTagProps) =>
              values.map((entity, index) => {
                const label = getEntityLabel(entity);
                const tagProps = getTagProps({ index });

                return (
                  <Tooltip
                    key={String(tagProps.key ?? getWidgetEntityKey(entity))}
                    title={label}
                    placement="top"
                  >
                    <span {...tagProps}>{label}</span>
                  </Tooltip>
                );
              })
          : undefined
      }
      size="small"
      renderInput={(params) => (
        <TextField {...params} label="Entités" size="small" />
      )}
      data-testid="entity-selector"
    />
  );

  const content = (
    <>
      {filterDefinitions.length > 0 && (
        <Autocomplete
          multiple
          options={allFilterOptions}
          value={selectedFilterOptions}
          onChange={(_, values) => handleFiltersChange(values)}
          isOptionEqualToValue={sameFilterOption}
          groupBy={(option) => toFrenchGroupLabel(option.label, option.key)}
          getOptionLabel={getFilterOptionLabel}
          renderTags={renderFilterTags}
          renderOption={(props, option) =>
            renderEllipsizedOption(props, getFilterOptionLabel(option))
          }
          size="small"
          renderInput={(params) => (
            <TextField {...params} label="Filtres" size="small" />
          )}
        />
      )}
      {entitySelector}
    </>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      p={matchControlPanelStyle ? 2 : 0}
      width={matchControlPanelStyle ? 300 : undefined}
    >
      {matchControlPanelStyle ? (
        <>
          <Typography
            variant="subtitle2"
            sx={{ fontSize: '0.9rem', fontWeight: 700 }}
          >
            Entités
          </Typography>
          <Box sx={controlPanelSelectorBoxSx}>{content}</Box>
        </>
      ) : (
        content
      )}
    </Box>
  );
};
